import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { cpf, cnpj } from 'cpf-cnpj-validator';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { SelectButton } from 'primereact/selectbutton';
import { Toast } from 'primereact/toast';

import { useFormCreateCreditor } from 'src/controllers/Creditor/CreateCreditorController';
import LoadingScreen from 'src/components/LoadingScreen';
import { useHistory } from 'react-router-dom';

interface InputMaskProps {
	label: string;
	mask: string;
	placeholder: string;
}

const CreditorForm: React.FC = () => {
	const { getCreditorFormController, handleCreditorFormController } = useFormCreateCreditor();
	const [inputMask, setInputMask] = useState<InputMaskProps>({
		label: 'CPF',
		mask: '999.999.999-99',
		placeholder: '___.___.___-__',
	});

	const [registerFinished, setRegisterFinished] = useState(false);

	const history = useHistory();
	const checkCpfCnpj =
		cpf.isValid(getCreditorFormController.formik.values.CPF_CNPJ || '') ||
		cnpj.isValid(getCreditorFormController.formik.values.CPF_CNPJ || '');

	useEffect(() => {
		if (getCreditorFormController.creditorResponse?.status === 200) {
			setRegisterFinished(true);
		}
		if (
			getCreditorFormController.creditorEditedResponse?.status === 200 &&
			!getCreditorFormController.creditorResponse
		) {
			setRegisterFinished(false);
		}
	}, [getCreditorFormController.creditorResponse, getCreditorFormController.creditorEditedResponse]);

	const maskOptions = [
		{ label: 'CPF', mask: '999.999.999-99', placeholder: '___.___.___-__' },
		{ label: 'CNPJ', mask: '99.999.999/9999-99', placeholder: '__.___.___/____-__' },
	];

	return (
		<div style={{ height: '100%' }} className='card p-align-center p-justify-center'>
			<Toast ref={getCreditorFormController.toast} />
			<h5>Cadastro de credor</h5>
			{getCreditorFormController.loading ? <LoadingScreen color='#0388e5' /> : <></>}
			{getCreditorFormController.loadingCreditorCpf ? <LoadingScreen color='#0388e5' /> : <></>}
			<form className='p-grid p-fluid'>
				<div className='p-field p-col-12 p-md-4'>
					<label htmlFor='cpf'>Selecione CPF / CNPJ</label>
					<div className='p-inputgroup'>
						<SelectButton
							value={inputMask.label}
							onChange={(e) => {
								const selectedMask = maskOptions.filter((option) => {
									return option.label === e.value;
								});
								if (selectedMask.length) setInputMask(selectedMask[0]);
							}}
							options={maskOptions.map((option) => {
								return option.label;
							})}
						/>
						<InputMask
							id='cpf'
							maxlength={14}
							autoClear={false}
							mask={inputMask.mask}
							placeholder={inputMask.placeholder}
							{...getCreditorFormController.formik.getFieldProps('CPF_CNPJ')}
							onChange={(e) => getCreditorFormController.formik.setFieldValue('CPF_CNPJ', e.value)}
							onComplete={(e: any) => handleCreditorFormController.searchCreditorCpf(e.value)}
							disabled={getCreditorFormController.foundCreditorCpf || registerFinished}
						/>
					</div>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getCreditorFormController.formik.touched.CPF_CNPJ &&
								(getCreditorFormController.formik.errors.CPF_CNPJ || !checkCpfCnpj),
							'popup-form-inactive': !(
								getCreditorFormController.formik.touched.CPF_CNPJ &&
								(getCreditorFormController.formik.errors.CPF_CNPJ || !checkCpfCnpj)
							),
						})}>
						{!checkCpfCnpj
							? '* Informe CPF ou CNPJ válido'
							: getCreditorFormController.formik.errors.CPF_CNPJ}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-8'>
					<label htmlFor='nomeComp'>Nome completo</label>
					<InputText
						id='nomeComp'
						{...getCreditorFormController.formik.getFieldProps('NOME')}
						onChange={getCreditorFormController.formik.handleChange('NOME')}
						disabled={getCreditorFormController.foundCreditorCpf || registerFinished}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getCreditorFormController.formik.touched.NOME &&
								getCreditorFormController.formik.errors.NOME,
							'popup-form-inactive': !(
								getCreditorFormController.formik.touched.NOME &&
								getCreditorFormController.formik.errors.NOME
							),
						})}>
						{getCreditorFormController.formik.errors.NOME}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-4'>
					<label htmlFor='banco'>Banco</label>
					<InputText
						id='banco'
						maxLength={150}
						disabled={registerFinished}
						{...getCreditorFormController.formik.getFieldProps('BANCO')}
						onChange={getCreditorFormController.formik.handleChange('BANCO')}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getCreditorFormController.formik.touched.BANCO &&
								getCreditorFormController.formik.errors.BANCO,
							'popup-form-inactive': !(
								getCreditorFormController.formik.touched.BANCO &&
								getCreditorFormController.formik.errors.BANCO
							),
						})}>
						{getCreditorFormController.formik.errors.BANCO}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-4'>
					<label htmlFor='agencia'>Agencia</label>
					<InputText
						id='agencia'
						maxLength={20}
						disabled={registerFinished}
						{...getCreditorFormController.formik.getFieldProps('AGENCIA')}
						onChange={getCreditorFormController.formik.handleChange('AGENCIA')}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getCreditorFormController.formik.touched.AGENCIA &&
								getCreditorFormController.formik.errors.AGENCIA,
							'popup-form-inactive': !(
								getCreditorFormController.formik.touched.AGENCIA &&
								getCreditorFormController.formik.errors.AGENCIA
							),
						})}>
						{getCreditorFormController.formik.errors.AGENCIA}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-4'>
					<label htmlFor='conta'>Conta</label>
					<InputText
						id='conta'
						// maxlength={20}
						placeholder='________-_'
						disabled={registerFinished}
						{...getCreditorFormController.formik.getFieldProps('CONTA')}
						onChange={(e) => getCreditorFormController.formik.setFieldValue('CONTA', e.currentTarget.value)}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getCreditorFormController.formik.touched.CONTA &&
								getCreditorFormController.formik.errors.CONTA,
							'popup-form-inactive': !(
								getCreditorFormController.formik.touched.CONTA &&
								getCreditorFormController.formik.errors.CONTA
							),
						})}>
						{getCreditorFormController.formik.errors.CONTA}
					</div>
				</div>
			</form>

			<div className='p-fluid p-grid p-justify-between'>
				{getCreditorFormController.creditorResponse?.status === 200 && !registerFinished ? (
					<div className='p-col-12 p-md-4'>
						<Button
							type='button'
							label='Salvar Alterações'
							onClick={() => {
								getCreditorFormController.formik.handleSubmit();
							}}
						/>
					</div>
				) : getCreditorFormController.creditorResponse?.status === 200 && registerFinished ? (
					<div className='p-col-12 p-md-6 p-fluid p-grid'>
						<div className='p-col-12 p-md-5'>
							<Button
								type='button'
								label='Editar'
								onClick={() => {
									setRegisterFinished(false);
								}}
							/>
						</div>
						<div className='p-col-12 p-md-5'>
							<Button
								icon='pi pi-plus'
								type='button'
								className='p-button-success'
								label='Cadastrar novo'
								onClick={() => {
									getCreditorFormController.formik.resetForm();
									setRegisterFinished(false);
									handleCreditorFormController.setCreditorResponse(null);
								}}
							/>
						</div>
					</div>
				) : (
					<div className='p-col-12 p-md-4'>
						<Button
							type='button'
							label='Cadastrar'
							onClick={() => getCreditorFormController.formik.handleSubmit()}
						/>
					</div>
				)}

				<div className='p-col-12 p-md-6 p-fluid p-grid'>
					<div className='p-col-6'>
						<Button
							type='button'
							label='Limpar'
							onClick={() => {
								getCreditorFormController.formik.resetForm();
								handleCreditorFormController.setFoundCreditorCpf(false);
							}}
						/>
					</div>
					<div className='p-col-6'>
						<Button
							type='button'
							className='p-button-secondary'
							label='Voltar para listagem'
							onClick={() => history.goBack()}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CreditorForm;
